import { StateMachine } from "@/core/arch/state_machine";
import { ProvisioningRepository } from "./provisioning_repository";
import type { ProvisionInput, WizardStep } from "./types";
import {
  phoneValidator,
  removeUnnecessaryCharactersFromPhone,
} from "@cpos/shared/src/validation/phone";
import { validateMinimumAge } from "@cpos/shared/src/validation/identity";
import { validateBusinessNameFraud } from "@cpos/shared/src/validation/fraud-detection";

interface ProvisioningState {
  wizardStep: WizardStep;
  formData: Partial<ProvisionInput>;
  submitting: boolean;
  result: {
    provisionId?: string | null;
    status?: string | null;
    failedStep?: string | null;
    failedError?: string | null;
    errors?: (string | null)[] | null;
  } | null;
  fieldErrors: Record<string, string>;
}

const initialState: ProvisioningState = {
  wizardStep: "contact",
  formData: {
    businessCountry: "CA",
    businessType: "individual",
  },
  submitting: false,
  result: null,
  fieldErrors: {},
};

const WIZARD_STEPS: WizardStep[] = ["contact", "business", "address", "review"];

export class ProvisioningBloc extends StateMachine<ProvisioningState> {
  constructor(private repo: ProvisioningRepository) {
    super(initialState);
  }

  reset(): void {
    this.setState({
      ...initialState,
      formData: { businessCountry: "CA", businessType: "individual" },
    });
  }

  updateField<K extends keyof ProvisionInput>(
    field: K,
    value: ProvisionInput[K],
  ): void {
    const fieldStr = String(field);
    const clearedErrors = Object.fromEntries(
      Object.entries(this.state.fieldErrors).filter(
        ([k]) => k !== fieldStr && !k.startsWith(`${fieldStr}.`),
      ),
    );
    this.setState({
      ...this.state,
      formData: { ...this.state.formData, [field]: value },
      fieldErrors: clearedErrors,
    });
  }

  nextStep(): boolean {
    const errors = this.validateCurrentStep();
    if (Object.keys(errors).length > 0) {
      this.setState({ ...this.state, fieldErrors: errors });
      return false;
    }
    const idx = WIZARD_STEPS.indexOf(this.state.wizardStep);
    if (idx < WIZARD_STEPS.length - 1) {
      this.setState({
        ...this.state,
        wizardStep: WIZARD_STEPS[idx + 1],
        fieldErrors: {},
      });
    }
    return true;
  }

  prevStep(): void {
    const idx = WIZARD_STEPS.indexOf(this.state.wizardStep);
    if (idx > 0) {
      this.setState({
        ...this.state,
        wizardStep: WIZARD_STEPS[idx - 1],
        fieldErrors: {},
      });
    }
  }

  goToStep(step: WizardStep): void {
    this.setState({ ...this.state, wizardStep: step, fieldErrors: {} });
  }

  async submit(): Promise<void> {
    this.setState({ ...this.state, submitting: true, result: null });
    try {
      const formData = this.state.formData;
      const normalized: ProvisionInput = {
        ...(formData as ProvisionInput),
        email: formData.email?.trim().toLowerCase() ?? "",
        phone: formData.phone
          ? removeUnnecessaryCharactersFromPhone(formData.phone.trim())
          : "",
        firstName: formData.firstName?.trim() ?? "",
        lastName: formData.lastName?.trim() ?? "",
        businessName: formData.businessName?.trim() ?? "",
        ...(formData.supportPhone && {
          supportPhone: removeUnnecessaryCharactersFromPhone(
            formData.supportPhone.trim(),
          ),
        }),
      };
      const result = await this.repo.provision(normalized);
      this.setState({ ...this.state, submitting: false, result });
    } catch (error) {
      this.setState({
        ...this.state,
        submitting: false,
        result: {
          errors: [
            error instanceof Error ? error.message : "Submission failed",
          ],
        },
      });
    }
  }

  private validateCurrentStep(): Record<string, string> {
    const { formData, wizardStep } = this.state;
    const errors: Record<string, string> = {};

    switch (wizardStep) {
      case "contact":
        if (!formData.firstName?.trim()) errors.firstName = "Required";
        if (!formData.lastName?.trim()) errors.lastName = "Required";
        if (!formData.email?.trim()) errors.email = "Required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
          errors.email = "Invalid email";
        if (!formData.phone?.trim()) {
          errors.phone = "Required";
        } else {
          const phoneError = phoneValidator(
            removeUnnecessaryCharactersFromPhone(formData.phone.trim()),
          );
          if (phoneError) errors.phone = phoneError;
        }
        break;
      case "business":
        if (!formData.businessName?.trim()) {
          errors.businessName = "Required";
        } else {
          const fraudResult = validateBusinessNameFraud(formData.businessName);
          if (!fraudResult.isValid) {
            errors.businessName = fraudResult.error || "Not allowed";
          }
        }
        if (!formData.businessCategoryCode?.trim())
          errors.businessCategoryCode = "Required";
        if (!formData.birthDate) {
          errors.birthDate = "Required";
        } else {
          const ageError = validateMinimumAge(formData.birthDate);
          if (ageError) errors.birthDate = ageError;
        }
        if (formData.businessCountry === "US" && !formData.ssnLast4?.trim())
          errors.ssnLast4 = "Required for US businesses";
        break;
      case "address":
        if (!formData.businessAddress?.line1?.trim()) {
          errors["businessAddress.line1"] = "Required";
        } else if (formData.businessAddress.line1.trim().length < 5) {
          errors["businessAddress.line1"] =
            "Please enter a complete address (minimum 5 characters)";
        }
        if (!formData.businessAddress?.city?.trim())
          errors["businessAddress.city"] = "Required";
        if (!formData.businessAddress?.state?.trim())
          errors["businessAddress.state"] = "Required";
        if (!formData.businessAddress?.postalCode?.trim()) {
          errors["businessAddress.postalCode"] = "Required";
        } else {
          const pc = formData.businessAddress.postalCode.trim();
          const country = formData.businessCountry ?? "CA";
          if (country === "CA" && !/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(pc)) {
            errors["businessAddress.postalCode"] =
              "Invalid postal code (expected: A1A 1A1)";
            // eslint-disable-next-line security/detect-unsafe-regex
          } else if (country === "US" && !/^\d{5}(-\d{4})?$/.test(pc)) {
            errors["businessAddress.postalCode"] =
              "Invalid ZIP code (expected: 12345)";
          }
        }
        if (!formData.individualAddress?.line1?.trim()) {
          errors["individualAddress.line1"] = "Required";
        } else if (formData.individualAddress.line1.trim().length < 5) {
          errors["individualAddress.line1"] =
            "Please enter a complete address (minimum 5 characters)";
        }
        if (!formData.individualAddress?.city?.trim())
          errors["individualAddress.city"] = "Required";
        if (!formData.individualAddress?.state?.trim())
          errors["individualAddress.state"] = "Required";
        if (!formData.individualAddress?.postalCode?.trim()) {
          errors["individualAddress.postalCode"] = "Required";
        } else {
          const pc = formData.individualAddress.postalCode.trim();
          const country = formData.businessCountry ?? "CA";
          if (country === "CA" && !/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(pc)) {
            errors["individualAddress.postalCode"] =
              "Invalid postal code (expected: A1A 1A1)";
            // eslint-disable-next-line security/detect-unsafe-regex
          } else if (country === "US" && !/^\d{5}(-\d{4})?$/.test(pc)) {
            errors["individualAddress.postalCode"] =
              "Invalid ZIP code (expected: 12345)";
          }
        }
        break;
    }

    return errors;
  }
}
